import StarIcon from '@mui/icons-material/Star';
import { Box } from '@mui/material';
import type React from 'react';

// Helper to truncate ID
export const formatDisplayId = (id: string | null | undefined) => {
  if (!id) {
    return 'N/A';
  }
  return id.length > 20 ? `${id.substring(0, 20)}...` : id;
};

// Helper to get tender source from ID
export const getTenderSourceString = (id: string | null | undefined) => {
  if (!id) {
    return '';
  }
  if (id.startsWith('VP')) {
    return 'VendorPanel';
  }
  if (id.startsWith('ATM')) {
    return 'AusTender';
  }
  if (id.startsWith('BNSW_')) {
    return 'Buy NSW';
  }
  if (id.startsWith('TVIC_')) {
    return 'Tenders Vic';
  }
  if (id.startsWith('TWA_')) {
    return 'Tenders WA';
  }
  if (id.startsWith('SAT_')) {
    return 'SA Tenders';
  }
  return '';
};

// Helper function to parse submissionDeadline string
export const parseSubmissionDeadlineInTable = (deadlineStr: string): Date | null => {
  if (!deadlineStr) {
    return null;
  }
  const dateTimeRegex = /(\d{1,2})\/([A-Za-z]{3})\/(\d{4})\s*(\d{1,2}):(\d{2})\s*(AM|PM)/;
  const offsetRegex = /UTC([+-]\d{2}:\d{2})?/;

  const dateTimeMatch = deadlineStr.match(dateTimeRegex);
  if (!dateTimeMatch) {
    return null;
  }

  const offsetMatch = deadlineStr.match(offsetRegex);
  const offsetString = offsetMatch?.[1] ? offsetMatch[1] : null;

  const [, dayStr, monthStr, yearStr, hourStr, minuteStr, ampm] = dateTimeMatch;

  const monthMap: { [key: string]: number } = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  const month = monthStr ? monthMap[monthStr as keyof typeof monthMap] : undefined;
  if (month === undefined) {
    return null;
  }

  let hour = parseInt(hourStr || '0', 10);
  if (ampm && ampm.toUpperCase() === 'PM' && hour !== 12) {
    hour += 12;
  } else if (ampm && ampm.toUpperCase() === 'AM' && hour === 12) {
    hour = 0;
  }

  if (offsetString) {
    const isoDateTimeString = `${yearStr}-${String(month + 1).padStart(2, '0')}-${String(dayStr).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${minuteStr}:00${offsetString}`;
    return new Date(isoDateTimeString);
  } else {
    const localDateTimeString = `${yearStr}-${String(month + 1).padStart(2, '0')}-${String(dayStr).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${minuteStr}:00`;
    return new Date(localDateTimeString);
  }
};

// Helper function to display days until deadline
export const displayDaysUntilDeadline = (deadlineDate: Date | null): string => {
  if (!deadlineDate || Number.isNaN(deadlineDate.getTime())) {
    return 'N/A';
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const deadlineDay = new Date(
    deadlineDate.getFullYear(),
    deadlineDate.getMonth(),
    deadlineDate.getDate()
  );

  const diffTime = deadlineDay.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'Closed';
  }
  if (diffDays === 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return '1 day';
  }
  return `${diffDays} days`;
};

// Helper function to check if tender is closed
export const isTenderClosed = (deadlineDate: Date | null): boolean => {
  if (!deadlineDate || Number.isNaN(deadlineDate.getTime())) {
    return false;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const deadlineDay = new Date(
    deadlineDate.getFullYear(),
    deadlineDate.getMonth(),
    deadlineDate.getDate()
  );

  const diffTime = deadlineDay.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays < 0;
};

// Get relevance stars based on calibrated score or explicit star count
export const getRelevanceStars = (
  score: number,
  starsOverride?: 0 | 1 | 2 | 3
): React.ReactNode => {
  const starCount =
    starsOverride !== undefined
      ? starsOverride
      : score >= 5.5
        ? 3
        : score >= 3.0
          ? 2
          : score >= 1.5
            ? 1
            : 0;

  if (starCount === 3) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
        <StarIcon sx={{ fontSize: '1rem', color: '#FF6800' }} />
        <StarIcon sx={{ fontSize: '1rem', color: '#FF6800' }} />
        <StarIcon sx={{ fontSize: '1rem', color: '#FF6800' }} />
      </Box>
    );
  }
  if (starCount === 2) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
        <StarIcon sx={{ fontSize: '1rem', color: '#FF6800' }} />
        <StarIcon sx={{ fontSize: '1rem', color: '#FF6800' }} />
      </Box>
    );
  }
  if (starCount === 1) {
    return <StarIcon sx={{ fontSize: '1rem', color: '#FF6800' }} />;
  }
  return null;
};
